import React, { FC, Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DetailPageLayout from "./Layout";
import { connect, useDispatch } from "react-redux";
import { updateLoadingIsStatus } from "../../redux/reducers/spinnerReducer";
import api from '../../api';
import { setBrand } from "../../redux/reducers/brandReducer";
import BrandDetailContainer from "./BrandDetailContainer";

const PageBrandDetailContainer = ({ isLoading, user }) => {
  const dispatch = useDispatch();
  const { shortname } = useParams();
  const thisPathname = useLocation().pathname;
  const [isBrandLoaded, setIsBrandLoaded] = useState(false);
  const [brandDetail, setBrandDetail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id && thisPathname.includes("view")) {
      window.location.href = '/brand/' + shortname;
    }
  }, [])

  useEffect(() => {
    if (localStorage.getItem('last_page') !== shortname) {
      localStorage.setItem('last_page', '/brand/' + shortname);
      setIsBrandLoaded(false);
    }
  },[shortname])

  useEffect(() => {
    loadBrandData();
  }, [shortname, user?.company?.id, isBrandLoaded]); // Dependencies to prevent infinite loop

  const loadBrandData = async () => {
    // Skip if already loaded or missing required data
    if (isBrandLoaded || !shortname) {
      return;
    }

    try {
      dispatch(updateLoadingIsStatus(true));
      setError(null);

      // Use your API's callback pattern
      let brandData;
      let getBrandPromise = null;

      if (user?.id) {
        // Promise wrapper for your callback-based API
        getBrandPromise = new Promise<any>((resolve, reject) => {
          api.getBrand(
            {
              shortname: shortname,
              sessionUser: user.company.id
            },
            (response: any) => {
              // Success callback - your API returns response object
              if (response && (response.data || response.status === 200)) {
                resolve(response);
              } else {
                reject(new Error('Invalid response from API'));
              }
            }
          );

          // Add timeout to prevent infinite waiting
          setTimeout(() => {
            reject(new Error('Request timeout'));
          }, 3000); // 3 second timeout
        });
      } else {
        // Promise wrapper for your callback-based API
        // Public view API call
        getBrandPromise = new Promise<any>((resolve, reject) => {
          api.getViewBrand(
            {
              shortname: shortname,
            },
            (response: any) => {
              // Success callback - your API returns response object
              if (response && (response.data || response.status === 200)) {
                resolve(response);
              } else {
                reject(new Error('Invalid response from API'));
              }
            }
          );

          // Add timeout to prevent infinite waiting
          setTimeout(() => {
            reject(new Error('Request timeout'));
          }, 3000); // 3 second timeout
        });
      }

      const response = await getBrandPromise;
      brandData = response.data || response;

      // Process the brand data
      if (brandData?.id) {
        brandData.imageUrls = brandData.images?.map((img) =>
          `${api.getUploadUrl()}/${img.url}`
        ) || [];

        dispatch(setBrand(brandData));
        setBrandDetail(brandData);
        setIsBrandLoaded(true);
      } else {
        setBrandDetail(null);
        setIsBrandLoaded(true);
        // throw new Error('No brand data received');
      }
      dispatch(updateLoadingIsStatus(false));

    } catch (err) {
      console.error('Error loading brand:', err);

      // Handle different types of errors
      let errorMessage = 'Failed to load brand details';

      if (err.message?.includes('404')) {
        errorMessage = 'Brand not found';
      } else if (err.message?.includes('Network')) {
        errorMessage = 'Network error - please check your connection';
      } else if (err.message?.includes('CORS')) {
        errorMessage = 'Connection error - please try again';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      dispatch(updateLoadingIsStatus(false));
    }
  };

  // Error state
  if (error) {
    return (
      <DetailPageLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">Unable to Load Brand</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-x-4">
              <button
                onClick={() => {
                  setError(null);
                  setIsBrandLoaded(false);
                }}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Try Again
              </button>
              <button
                onClick={() => window.history.back()}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  // Loading state
  if (!isBrandLoaded && !error) {
    return (
      <DetailPageLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading brand details...</p>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  // Success state
  return (
    <DetailPageLayout>
      {brandDetail ? (
        <BrandDetailContainer />
      ) : (
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-semibold mb-4">BRAND NOT FOUND</h2>
            <button
              onClick={() => window.history.back()}
              className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Go Back
            </button>
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  isLoading: state.spinner.isLoading,
  brand: state.brand,
});

export default connect(mapStateToProps, { updateLoadingIsStatus, setBrand })(
  PageBrandDetailContainer
);